<template>
  <div class="pt-16">
    <h2 class="pt-6 pb-3">Import Charge Files</h2>
    <v-container fluid pa-0 ma-0>
      <v-card>
        <v-row no-gutters style="height: 150px" align="center">
          <v-col cols="3" class="pl-4 pr-8">
            <v-menu
              v-model="datepicker"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="chargeDate"
                  label="Select Charge Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  clearable
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="chargeDate"
                @input="datepicker = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="6" class="pl-16">
            <v-file-input
              label="Import file"
              :disabled="chargeDate == null"
              v-model="file"
              @change="processFile"
            ></v-file-input>
          </v-col>
          <v-col>
            <v-btn
              class="primary"
              :disabled="disbleImportBtn"
              @click="importToUb"
              >{{ importBtnLabel }}</v-btn
            >
          </v-col>
        </v-row>
      </v-card>
      <v-overlay :value="processingImport">
        <v-progress-circular indeterminate color="primary" size="64"
          >Importing...</v-progress-circular
        >
      </v-overlay>
      <v-alert type="success" :value="importDone" text
        >{{ alertMessage }}
      </v-alert>
    </v-container>
    <v-container v-if="!showReport">
      <v-col cols="3" align="left">
        <span class="subtitle-1 pt-4 d-inline-block">Import Summary</span>
      </v-col>
      <v-alert v-if="errorMessage" text type="error">
        {{ errorMessage }}
      </v-alert>
      <v-row no-gutters class="py-2">
        <v-col class="text-right">
          <v-btn 
            class="primary" 
            @click="downloadReport(true)"
            :disabled="!reportGridApi && !(gridOptions && gridOptions.api)"
            >Download Summary</v-btn
          >
        </v-col>
      </v-row>
      <v-alert v-if="hasDataErrors" text type="error" class="mb-4">
        Some rows contain errors. Please review the highlighted rows for details.
        Any rows with errors will be excluded from the import process.
      </v-alert>
      <v-card>
        <ag-grid-vue
          style="height: 500px"
          class="ag-theme-balham"
          :columnDefs="columnDefs"
          :gridOptions="gridOptions"
          :defaultColDef="defaultColDef"
          :rowData="rowData"
        >
        </ag-grid-vue>
      </v-card>
      <v-row no-gutters class="py-4">
        <v-col class="text-right">
          <v-btn class="primary" @click="dialog = true"
            >Charge Exclusions</v-btn
          >
        </v-col>
      </v-row>
      <v-dialog v-if="dialog" v-model="dialog" persistent max-width="900">
        <charge-exclusions @onClose="onClose"></charge-exclusions>
      </v-dialog>
    </v-container>
    <v-container v-if="showReport">
      <v-row no-gutters class="py-2">
        <v-col class="text-right">
          <v-btn 
            class="primary" 
            @click="downloadReport(false)"
            :disabled="!reportGridApi && !(gridOptions && gridOptions.api)"
            >Download Report</v-btn
          >
        </v-col>
      </v-row>
      <v-card>
        <ag-grid-vue
          ref="reportGrid"
          style="height: 500px"
          class="ag-theme-balham"
          :columnDefs="reportColumnDefs"
          :gridOptions="gridOptions"
          :defaultColDef="defaultColDef"
          :rowData="reportData"
          @grid-ready="onReportGridReady"
        >
        </ag-grid-vue>
      </v-card>
    </v-container>
    <v-overlay :value="isLoading"></v-overlay>
    <v-dialog v-model="isLoading" hide-overlay persistent width="300">
      <v-card class="py-4">
        <v-card-text class="pb-0">
          Processing file uploaded
          <v-progress-linear indeterminate class="mb-0"></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import { AgGridVue } from "ag-grid-vue";
import moment from "moment";
import { HTTP } from "@/http-common";
import parse from "csv-parse";
import ChargeExclusions from "@/components/charge-exclusions";

export default {
  name: "ImportChargeFile",
  components: {
    AgGridVue,
    ChargeExclusions,
  },
  data: () => ({
    gridApi: {},
    gridColumnApi: {},
    gridOptions: {},
    defaultColDef: {
      resizable: true,
    },
    columnDefs: null,
    file: null,
    dialog: false,
    rowData: [],
    errorMessage: null,
    fileContents: null,
    importBtnLabel: "Import to UB",
    alertMessage: "",
    disbleImportBtn: true,
    processingImport: false,
    importDone: false,
    datepicker: false,
    chargeDate: null,
    isLoading: false,
    showReport: false,
    reportData: [],
    reportGridApi: null,
    reportColumnDefs: []

  }),
  mounted() {
    let vm = this;
    vm.gridApi = vm.gridOptions.api;
    vm.gridColumnApi = vm.gridOptions.columnApi;
    vm.gridApi.sizeColumnsToFit();
  },
  computed: {
    hasDataErrors() {
      return this.rowData && this.rowData.some(row => row.reason);
    }
  },
  beforeMount() {
    let vm = this;
    vm.gridOptions = { 
      defaultToolPanel: "",
      getRowStyle: (params) => {
        if (params.data && (params.data.reason)) {
          return { color: 'red' };
        }
        return null;
      }
    };
    vm.columnDefs = [
      {
        headerName: "Service ID",
        field: "service_ID",
        sortable: true
      },
      {
        headerName: "UB Customer No",
        field: "ubCustNo",
        sortable: true
      },
      {
        headerName: "Description",
        field: "description",
        sortable: true
      },
      {
        headerName: "Amount",
        field: "amount",
        sortable: true,
        cellRenderer: (params) => {
          if (!params.value == undefined) {
            return params.value.toFixed(2);
          }
          return params.value;
        },
      },
      {
        headerName: "Charge Date",
        field: "chargeDate",
        sortable: true
      },
    ];
  },
  methods: {
    onGridReady(args) {
      let vm = this;
      vm.gridApi = args.api;
      vm.gridApi.sizeColumnsToFit();
    },
    processFile() {
      this.isLoading = true;
      this.importDone = false;
      this.showReport = false;
      this.reportData = [];
      if (this.file != null) {
        this.disbleImportBtn = true;
        this.importBtnLabel = "Processing file...";
        this.readCSV();
        this.upload();
      }
    },
    parseCSV() {
      return new Promise((resolve, reject) => {
        var reader = new FileReader();
        let vm = this;
        reader.readAsText(this.file);
        reader.onload = (e) => {
          parse(
            reader.result,
            { columns: true, skip_empty_lines: true },
            function(err, output) {
              if (err) {
                vm.errorMessage = err;
                reject(err);
              } else {
                vm.rowData = output;
                resolve(output);
              }
            }
          );
        };
      });
    },
    async readCSV() {
      let vm = this;
      if (!vm.file.name.toLowerCase().includes("csv")) {
        return;
      }
      try {
        this.fileContents = await this.parseCSV();
        this.columnDefs = [
          {
            headerName: "Service ID",
            field: "OM_Service_ID",
            sortable: true
          },
          {
            headerName: "Value Description",
            field: "valuedescription",
            sortable: true
          },
          {
            headerName: "Rated Amount",
            field: "Rated_Amount",
            sortable: true
          },
          {
            headerName: "Period Start",
            field: "PeriodStart",
            sortable: true
          },
          {
            headerName: "Period End",
            field: "PeriodEnd",
            sortable: true
          },
        ];
      } catch (error) {
        this.errorMessage = error;
      }
    },
    async upload() {
      let vm = this;
      this.errorMessage = null;
      let formData = new FormData();
      if (vm.file.name.toLowerCase().includes("csv") && !vm.rowData) {
        vm.fileContents = await this.parseCSV();
      }

      formData.append("file", this.file);
      formData.append("chargeDate", this.chargeDate);
      this.importFile(formData)
        .then((data) => {
          this.rowData = data.data;
          this.gridApi.hideOverlay();
          this.importBtnLabel = "Import to UB";
          this.disbleImportBtn = false;
          this.gridApi.sizeColumnsToFit();
          this.isLoading = false;
        })
        .catch((err) => {
          this.errorMessage = err.response.data.message;
          this.gridApi.hideOverlay();
          this.processingImport = false;
        });
    },
    importFile(formData) {
      let vm = this;
      this.gridApi.showLoadingOverlay();
      return new Promise((resolve, reject) => {
        let response = HTTP.post("BulkUpload/importChargeFile", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        response
          .then((data) => {
            vm.columnDefs = [
              {
                headerName: "Service ID",
                field: "service_ID",
                sortable: true
              },
              {
                headerName: "UB Customer No",
                field: "ubCustNo",
                sort: "desc",
                sortable: true
              },
              {
                headerName: "Description",
                field: "description",
                sort: "desc",
                sortable: true
              },
              {
                headerName: "Amount",
                field: "amount",
                sortable: true,
                cellRenderer: (params) => {
                  if (params.hasOwnProperty("value"))
                    return params.value.toFixed(2);
                },
              },
              {
                headerName: "Charge Date",
                field: "chargeDate",
                sortable: true,
                cellRenderer: (params) => {
                  if (params.hasOwnProperty("value"))
                    return moment(params.value).format("MMM D, YYYY");
                },
              },
              {
                headerName: "Success",
                field: "success",
                sortable: true,
                hide: true
              },
              {
                headerName: "Error",
                field: "reason",
                sortable: true,
                width: 150,
              },
            ];
            resolve(data);
          })
          .catch((err) => {
            this.processingImport = false;
            this.gridApi.hideOverlay();

            reject(err);
          });
      });
    },
    importToUb() {
      this.disbleImportBtn = true;
      this.processingImport = true;
      HTTP.post("BulkUpload/importChargesToUtilibill", this.rowData)
        .then((data) => {
          this.file = null;
          this.alertMessage =
            "Import done. " + data.data.length + " rows imported.";
          this.processingImport = false;
          this.importDone = true;
          this.chargeDate = null;
          this.processReportData(data.data);
          this.showReport = true;
        })
        .catch((err) => {
          this.errorMessage = err.response.data.message;
          this.gridApi.hideOverlay();
          this.processingImport = false;
        });
    },
    processReportData(apiResponse) {
      this.reportData = apiResponse.map((item) => {
        const returnData = item.return || item;
        return {
          serviceNo: returnData.serviceNo || null,
          custNo: returnData.custNo || null,
          description: returnData.description || null,
          charge: returnData.charge || null,
          success: !returnData.isError,
          errorDescription: returnData.errorDescription || '',
        };
      });
      
      if (this.reportColumnDefs.length === 0) {
        this.reportColumnDefs = [
          {
            headerName: "Service No",
            field: "serviceNo",
            sortable: true,
          },
          {
            headerName: "Customer No",
            field: "custNo",
            sortable: true,
          },
          {
            headerName: "Description",
            field: "description",
            sortable: true,
          },
          {
            headerName: "Charge",
            field: "charge",
            sortable: true,
            cellRenderer: (params) => {
              if (params.value != null && params.value !== undefined) {
                return parseFloat(params.value).toFixed(2);
              }
              return params.value;
            },
          },
          {
            headerName: "Success",
            field: "success",
            sortable: true,
            hide: false
          },
          {
            headerName: "Error",
            field: "errorDescription",
            sortable: true,
            width: 150,
          },
        ];
      }
      
      this.$nextTick(() => {
        if (this.reportGridApi) {
          this.reportGridApi.sizeColumnsToFit();
        }
      });
    },
    onReportGridReady(args) {
      this.reportGridApi = args.api;
      if (this.gridOptions) {
        this.gridOptions.api = args.api;
      }
      if (this.reportGridApi) {
        this.reportGridApi.sizeColumnsToFit();
      }
    },
    downloadReport(isSummary = false) {
      let api = this.reportGridApi;
      
      if (!api && this.gridOptions && this.gridOptions.api) {
        api = this.gridOptions.api;
      }
      
      if (!api && this.$refs.reportGrid) {
        if (this.$refs.reportGrid.$refs && this.$refs.reportGrid.$refs.gridApi) {
          api = this.$refs.reportGrid.$refs.gridApi;
        } else if (this.$refs.reportGrid.api) {
          api = this.$refs.reportGrid.api;
        } else if (this.$refs.reportGrid.gridApi) {
          api = this.$refs.reportGrid.gridApi;
        }
      }
      
      if (api) {
        const timestamp = moment().format("YYYY-MM-DD_HH-mm-ss");
        const params = {
          fileName: `import-charge-${isSummary ? "summary" : "report"}-${timestamp}.csv`,
        };
        api.exportDataAsCsv(params);
      } else {
        console.warn("Report grid API not available. Waiting for grid to initialize...");
        this.$nextTick(() => {
          if (this.reportGridApi || (this.gridOptions && this.gridOptions.api)) {
            this.downloadReport();
          }
        });
      }
    },
    onClose() {
      this.dialog = false;
    },
  },
};
</script>
